// client/src/components/common/Header.jsx

import React, { useState } from 'react';
import { Phone, X, FileBarChart, ShieldCheck } from 'lucide-react';
import SearchBox from './SearchBox';
import { apiClient } from '../../services/apiClient';
import SubscriptionStatusModal from './SubscriptionStatusModal';

const Header = ({
  currentUser,
  connectionStatus,
  getStatusColor,
  getStatusText,
  handleLogout,
  reconnectAttempts,
  MAX_RECONNECT_ATTEMPTS,
  connectWebSocket,
  onSearch,
  isSearching,
  searchEnabled = false
}) => {
  const [isGeneratingToken, setIsGeneratingToken] = useState(false);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);

  const handleReportsClick = async () => {
    if (isGeneratingToken) return;

    try {
      setIsGeneratingToken(true);
      console.log('🚀 Requesting report handshake token...');

      const response = await apiClient.post('/handoff/report-token');

      if (response.data.success && response.data.token) {
        const { token, user, email } = response.data;
        // const reportsUrl ='https://dev-report.bozarthconnect.com/auth';
        const reportsUrl = import.meta.env.VITE_BOZARTH_REPORTS_URL;
        const finalUrl = `${reportsUrl}?token=${token}&user=${user}&email=${encodeURIComponent(email)}`;

        console.log('✅ Handshake token received. Redirecting to:', reportsUrl);

        window.open(finalUrl, '_blank');
      } else {
        console.error('❌ Failed to get handshake token:', response.data.message);
        alert('Failed to generate secure reports session. Please try again.');
      }
    } catch (error) {
      console.error('❌ Error during report handshake:', error);
      alert('An error occurred while connecting to the reports module.');
    } finally {
      setIsGeneratingToken(false);
    }
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-50">
      {/* Gradient Header */}
      <div
        className="text-white px-4 py-2"
        style={{
          background: 'linear-gradient(to right, #1b3378 5%, #192f6b 15%, #17295b 25%, #15244c 35%, #132143 50%, #111c39 70%, #0f182d 90%)'
        }}
      >
        <div className="flex items-center justify-between">
          {/* Left Side - Logo and Branding */}
          <div className="flex items-center">
            <img
              src="/logo/logo-no-bg.webp"
              alt="Bozarth Connect"
              className="h-20 w-20 object-contain flex-shrink-0 -my-4"
            />
            <div className="ml-3">
              <div className="text-2xl font-bold tracking-wide leading-tight">
                BOZARTH
              </div>
              <div className="text-lg font-semibold leading-tight">
                Connect
              </div>
            </div>
            <div className="border-l border-white/40 h-12 mx-6"></div>
          </div>

          {/* Center - Call Information */}
          <div className="flex-1 flex items-center gap-6 pl-0">
            {/* Conditionally Render SearchBox */}
            {searchEnabled && (
              <>
                <div className="w-80">
                  <SearchBox
                    onSearch={onSearch}
                    isSearching={isSearching}
                  />
                </div>

                <div className="border-l border-white/40 h-12"></div>
              </>
            )}

            {/* Call Information */}
            <div className="flex flex-col">
              <h2 className="text-xl font-medium tracking-tight">Customer Information</h2>
              <div className="flex items-center gap-1.5 mt-1">
                <Phone className="w-3.5 h-3.5" />
                <span className="text-sm font-light">
                  {searchEnabled ? 'Call Tracking & Search' : 'Call Tracking'}
                </span>
              </div>
            </div>
          </div>

          {/* Right Side - Extension, Connection Status, Logout Button */}
          <div className="flex items-center gap-3">
            <button
              disabled={isGeneratingToken}
              className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${isGeneratingToken
                ? 'bg-blue-500/10 text-blue-300 cursor-not-allowed'
                : 'bg-blue-500/20 text-blue-100 hover:bg-blue-500/30'
                }`}
              onClick={handleReportsClick}
            >
              {isGeneratingToken ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                  Connecting...
                </>
              ) : (
                <>
                  <FileBarChart className="w-4 h-4" />
                  Reports
                </>
              )}
            </button>

            {/* {searchEnabled && (
              <span className="bg-yellow-500/20 text-yellow-100 px-3 py-1.5 rounded-lg text-xs font-medium">
                TEST MODE
              </span>
            )} */}

            {currentUser && (
              <span className="bg-white/20 text-white px-3 py-1.5 rounded-lg text-sm font-medium">
                Ext: {currentUser.username}
              </span>
            )}

            {connectionStatus && (
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium ${getStatusColor() === 'connected' ? 'bg-green-500/20 text-green-100' :
                getStatusColor() === 'reconnecting' ? 'bg-yellow-500/20 text-yellow-100' :
                  'bg-red-500/20 text-red-100'
                }`}>
                <span className={`w-2 h-2 rounded-full ${getStatusColor() === 'connected' ? 'bg-green-300' :
                  getStatusColor() === 'reconnecting' ? 'bg-yellow-300' :
                    'bg-red-300'
                  }`}></span>
                <span className="capitalize">{getStatusText()}</span>
              </div>
            )}

            {connectionStatus === 'failed' && (
              <button
                className="bg-yellow-500/20 text-yellow-100 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-yellow-500/30 transition-colors"
                onClick={() => {
                  connectWebSocket();
                }}
              >
                Retry
              </button>
            )}

            {currentUser && (
              <button
                onClick={() => setIsSubscriptionModalOpen(true)}
                className="flex items-center gap-2 bg-emerald-500/20 text-emerald-100 px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-emerald-500/30 transition-colors"
                title="View my subscription status"
              >
                <ShieldCheck className="w-4 h-4" />
                Subscriptions
              </button>
            )}

            {handleLogout && (
              <button
                onClick={handleLogout}
                className="bg-red-500/20 text-red-100 px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-red-500/30 transition-colors"
              >
                Logout
              </button>
            )}
          </div>
        </div>
      </div>

      <SubscriptionStatusModal
        isOpen={isSubscriptionModalOpen}
        onClose={() => setIsSubscriptionModalOpen(false)}
        currentUser={currentUser}
      />
    </div>
  );
};

export default Header;